import { useCallback, useEffect, useState } from 'react'

import { Button, Modal, TermsOfService } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { Role } from '@circlefin/permissions/roles'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  InAppNotificationType,
  useFilteredLongPollInAppNotificationLazyQuery,
  useOnboardingSubmitApplicationMutation,
} from '@shared/graphql'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'

import { msaSanitizeOptions, MsaModalType } from '../utils'

import type { MsaOperationResponse } from '@shared/graphql'

export interface SignMsaProps {
  /**
   * MSA type.
   */
  msaType?: MsaModalType
  /**
   * Operation data.
   */
  data?: MsaOperationResponse
  /**
   * On submit callback.
   */
  onSubmit?: () => void
}

export const SignMsa: React.FC<SignMsaProps> = ({
  msaType = MsaModalType.core,
  data,
  onSubmit,
}) => {
  const { t } = useTranslation('developer')
  const modal = useModal()
  const { data: sessionData, update } = useSession()
  const [submitting, setSubmitting] = useState(false)

  // Sign MSA and trigger onSubmit callback
  const [signMsa, signMsaResult] = useOnboardingSubmitApplicationMutation()

  const [startSignedNotificationPolling, { refetch }] =
    useFilteredLongPollInAppNotificationLazyQuery({
      variables: {
        input: {
          type:
            msaType === MsaModalType.core
              ? InAppNotificationType.coreApiAgreementSigned
              : InAppNotificationType.crossCurrencyApiTermsAndConditionsSigned,
        },
      },
      onCompleted: (data) => {
        if (data.filteredLongPollInAppNotification != null) {
          void update()
        } else {
          void refetch()
        }
      },
    })

  useEffect(() => {
    // If user has role, their session is updated client side, so trigger callback
    const role =
      msaType === MsaModalType.core
        ? Role.CREATE_API_KEY_ROLE
        : Role.CROSS_CURRENCY_API_ROLE

    if (sessionData?.roles?.includes(role)) {
      setSubmitting(false)
      onSubmit?.()
    }
  }, [msaType, onSubmit, sessionData?.roles])

  const operation = data?.operation
  const certification = data?.certification

  const handleSubmit = useCallback(() => {
    if (operation?.onboardingId && certification?.id) {
      setSubmitting(true)

      void signMsa({
        variables: {
          input: {
            onboardingId: operation.onboardingId,
            certificationIds: [certification.id],
          },
        },
        onCompleted: () => {
          void startSignedNotificationPolling()
        },
        onError: () => {
          setSubmitting(false)
        },
      })
    }
  }, [
    certification?.id,
    operation?.onboardingId,
    signMsa,
    startSignedNotificationPolling,
  ])

  const [isScrollCompleted, setIsScrollCompleted] = useState(false)

  const handleScrollCompleted = useCallback(() => {
    setIsScrollCompleted(true)
  }, [])

  const handleCancel = useCallback(() => {
    modal.close()
  }, [modal])

  return (
    <GraphQLErrorBoundary
      error={signMsaResult.error}
      retry={signMsaResult.reset}
    >
      <Modal.Title>{t(`msa.${msaType}.heading`)}</Modal.Title>
      <Modal.Subtitle>{t(`msa.${msaType}.description`)}</Modal.Subtitle>
      <Modal.Body className="mt-4 h-128 w-full">
        <TermsOfService>
          <TermsOfService.Scroll
            isScrollCompleted={isScrollCompleted}
            onScrollCompleted={handleScrollCompleted}
          >
            <TermsOfService.Content
              html={certification?.content ?? ''}
              sanitizeOptions={msaSanitizeOptions}
            />
          </TermsOfService.Scroll>
        </TermsOfService>
      </Modal.Body>
      <Modal.Footer>
        <div className="mt-6 flex flex-row justify-end space-x-4">
          <Button onClick={handleCancel} variant="secondary">
            {t`common:cancel`}
          </Button>

          <Button
            disabled={!isScrollCompleted}
            loading={signMsaResult.loading || submitting}
            onClick={handleSubmit}
            variant="primary"
          >
            {t(`msa.${msaType}.accept`)}
          </Button>
        </div>
      </Modal.Footer>
    </GraphQLErrorBoundary>
  )
}
